import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import { FORGE_ORIGIN, getForgeToken } from 'shared/api/forgeApi'

import { ArtifactView } from '../types'

const READY_TIMEOUT_MS = 12000

type BridgeStatus = 'loading' | 'ready' | 'error'

interface BridgeOptions {
  iframeRef: RefObject<HTMLIFrameElement>
  view: ArtifactView
  context?: unknown
  onRefine?: () => void
}

interface BridgeApi {
  reload: () => void
  status: BridgeStatus
  retry: () => void
}

export const useArtifactBridge = ({
  iframeRef,
  view,
  context,
  onRefine
}: BridgeOptions): BridgeApi => {
  const [status, setStatus] = useState<BridgeStatus>('loading')
  // holds the latest ready flag so view/context/reload effects can guard against
  // posting before clusterix:ready arrives (the iframe would drop those messages)
  const readyRef = useRef(false)

  // ref keeps latest values without re-binding the message listener each render
  const stateRef = useRef({ view, context, onRefine })
  stateRef.current = { view, context, onRefine }

  const post = useCallback(
    (msg: Record<string, unknown>) => {
      const win = iframeRef.current?.contentWindow
      if (win && FORGE_ORIGIN) win.postMessage(msg, FORGE_ORIGIN)
    },
    [iframeRef]
  )

  useEffect(() => {
    // clusterix:ready can fire before this listener attaches, so check on mount
    const onMessage = (event: MessageEvent) => {
      if (FORGE_ORIGIN && event.origin !== FORGE_ORIGIN) return
      if (event.source !== iframeRef.current?.contentWindow) return
      const data = event.data as { type?: string; [k: string]: unknown }
      switch (data.type) {
        case 'clusterix:ready':
          readyRef.current = true
          setStatus('ready')
          post({
            type: 'clusterix:init',
            token: getForgeToken(),
            view: stateRef.current.view,
            context: stateRef.current.context ?? null
          })
          break
        case 'clusterix:refine':
          stateRef.current.onRefine?.()
          break
        default:
          break
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [iframeRef, post])

  // single timer: if the iframe never reports ready within READY_TIMEOUT_MS,
  // flip to error so the offline cover appears (cross-origin iframes don't fire
  // onError reliably, hence the manual timeout)
  useEffect(() => {
    if (status === 'ready') return
    const t = setTimeout(() => {
      if (!readyRef.current) setStatus('error')
    }, READY_TIMEOUT_MS)
    return () => clearTimeout(t)
  }, [status])

  // push view changes, no reload
  useEffect(() => {
    if (readyRef.current) post({ type: 'clusterix:view', view })
  }, [view, post])

  useEffect(() => {
    if (readyRef.current)
      post({ type: 'clusterix:context', context: context ?? null })
  }, [context, post])

  // no iframe reload, just asks widget to re-fetch
  const reload = useCallback(() => {
    if (readyRef.current) post({ type: 'clusterix:reload' })
  }, [post])

  // clusterix:reload can't recover a failed load, so re-assign src. about:blank
  // round-trip is needed because re-assigning the same src is a no-op in browsers
  const retry = useCallback(() => {
    const el = iframeRef.current
    if (!el) return
    const { src } = el
    el.src = 'about:blank'
    el.src = src
    readyRef.current = false
    setStatus('loading')
  }, [iframeRef])

  return { reload, status, retry }
}
